// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface InlineDeleteConfirmProps {
  /** Called when the user confirms deletion. */
  onConfirm: () => void;
  /** Whether the delete operation is currently in progress. */
  isDeleting: boolean;
  /** Accessible label for the delete button. */
  ariaLabel?: string;
}

/**
 * Inline delete button with a confirm/cancel step.
 *
 * Default state: trash icon button.
 * Confirming state: "Delete?" label + Confirm (red) + Cancel buttons.
 * Deleting state: spinner on the Confirm button.
 */
export function InlineDeleteConfirm({
  onConfirm,
  isDeleting,
  ariaLabel = 'Delete',
}: InlineDeleteConfirmProps) {
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <>
        <span className="mr-1 text-[13px] text-destructive">Delete?</span>
        <button
          type="button"
          onClick={() => {
            onConfirm();
            // Keep confirming=true; parent resets isDeleting on completion.
          }}
          disabled={isDeleting}
          className="rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 font-medium text-destructive-foreground bg-destructive transition-colors hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        >
          {isDeleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            'Confirm'
          )}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Cancel
        </button>
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      disabled={isDeleting}
      className="rounded-md p-2.5 sm:p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
      aria-label={ariaLabel}
    >
      {isDeleting ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Trash2 className="h-3.5 w-3.5" />
      )}
    </button>
  );
}
